import { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const AnswersReport = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchReport = (e) => {
    e.preventDefault();

    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates");
      return;
    }

    // Format dates for API
    const formattedStartDate = startDate.toISOString().split('T')[0];
    const formattedEndDate = endDate.toISOString().split('T')[0];

    setLoading(true);
    axios
      .get("/questions/answers-report", {
        params: { startDate: formattedStartDate, endDate: formattedEndDate },
      })
      .then((res) => {
        setReport(res.data.report);
        setHasSearched(true);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        toast.error(
          err?.response?.data?.errors?.[0] || "Failed to fetch report"
        );
        setLoading(false);
      });
  };

  const exportToExcel = () => {
    if (report.length === 0) {
      toast.error("No data to export");
      return;
    }

    const data = [];
    report.forEach((question) => {
      // Add question header
      data.push({
        Question: question.english,
        'Question (Arabic)': question.arabic || 'N/A',
        'Release Date': new Date(question.releaseDate).toLocaleDateString(),
        'User Name': '',
        'User Email': '',
        'Selected Option': '',
        'Result': ''
      });

      // Add correct answers
      question.correctAnswers.forEach((user) => {
        data.push({
          Question: '',
          'Question (Arabic)': '',
          'Release Date': '',
          'User Name': user.name,
          'User Email': user.email,
          'Selected Option': user.selectedOption,
          'Result': 'Correct'
        });
      });

      // Add incorrect answers
      question.incorrectAnswers.forEach((user) => {
        data.push({
          Question: '',
          'Question (Arabic)': '',
          'Release Date': '',
          'User Name': user.name,
          'User Email': user.email,
          'Selected Option': user.selectedOption,
          'Result': 'Incorrect'
        });
      });

      // Add empty row for separation
      data.push({
        Question: '',
        'Question (Arabic)': '',
        'Release Date': '',
        'User Name': '',
        'User Email': '',
        'Selected Option': '',
        'Result': ''
      });
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Answers Report");
    const formattedStartDate = startDate.toISOString().split('T')[0];
    const formattedEndDate = endDate.toISOString().split('T')[0];
    XLSX.writeFile(workbook, `Answers_Report_${formattedStartDate}_to_${formattedEndDate}.xlsx`);
    toast.success("Excel file downloaded successfully!");
  };

  const exportToPDF = () => {
    if (report.length === 0) {
      toast.error("No data to export");
      return;
    }

    const doc = new jsPDF();
    let yPosition = 20;

    doc.setFontSize(18);
    doc.text("Answers Report", 14, yPosition);
    yPosition += 10;
    doc.setFontSize(12);
    doc.text(`Period: ${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`, 14, yPosition);
    yPosition += 10;

    report.forEach((question, index) => {
      // Check if we need a new page
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text(`Question ${index + 1}:`, 14, yPosition);
      yPosition += 7;
      
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      const splitQuestion = doc.splitTextToSize(question.english, 180);
      doc.text(splitQuestion, 14, yPosition);
      yPosition += (splitQuestion.length * 5) + 3;

      doc.setFontSize(10);
      doc.text(`Date: ${new Date(question.releaseDate).toLocaleDateString()}`, 14, yPosition);
      yPosition += 7;

      // Correct answers table
      if (question.correctAnswers.length > 0) {
        autoTable(doc, {
          startY: yPosition,
          head: [['Name', 'Email', 'Selected Option']],
          body: question.correctAnswers.map(user => [user.name, user.email, user.selectedOption]),
          theme: 'grid',
          headStyles: { fillColor: [34, 197, 94] },
          margin: { left: 14 },
          styles: { fontSize: 9 }
        });
        yPosition = doc.lastAutoTable.finalY + 5;
      }

      // Incorrect answers table
      if (question.incorrectAnswers.length > 0) {
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        autoTable(doc, {
          startY: yPosition,
          head: [['Name', 'Email', 'Selected Option']],
          body: question.incorrectAnswers.map(user => [user.name, user.email, user.selectedOption]),
          theme: 'grid',
          headStyles: { fillColor: [239, 68, 68] },
          margin: { left: 14 },
          styles: { fontSize: 9 }
        });
        yPosition = doc.lastAutoTable.finalY + 10;
      } else {
        yPosition += 5;
      }
    });

    const formattedStartDate = startDate.toISOString().split('T')[0];
    const formattedEndDate = endDate.toISOString().split('T')[0];
    doc.save(`Answers_Report_${formattedStartDate}_to_${formattedEndDate}.pdf`);
    toast.success("PDF file downloaded successfully!");
  };

  return (
    <div className="py-20 sm:py-24 px-4 sm:px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 sm:mb-8 gap-3">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800">Answers Report</h1>
          <Link to="/questions-list">
            <button className="text-emerald-600 underline decoration-emerald-200 hover:decoration-emerald-500 hover:text-emerald-700 font-medium transition-colors text-sm sm:text-base">
              Back to Questions
            </button>
          </Link>
        </div>

        <div className="bg-white/80 backdrop-blur-2xl border border-white/40 rounded-2xl shadow-xl p-4 sm:p-6 mb-6 sm:mb-8">
          <form onSubmit={fetchReport} className="flex gap-3 sm:gap-4 items-end flex-wrap">
            <div className="flex-1 min-w-[160px]">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Start Date
              </label>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                maxDate={endDate || new Date()}
                placeholderText="Select start date"
                dateFormat="yyyy-MM-dd"
                className="w-full px-3 sm:px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white/60 text-sm sm:text-base transition-all"
                wrapperClassName="w-full"
                required
              />
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                End Date
              </label>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                maxDate={new Date()}
                placeholderText="Select end date"
                dateFormat="yyyy-MM-dd"
                className="w-full px-3 sm:px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white/60 text-sm sm:text-base transition-all"
                wrapperClassName="w-full"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="px-5 sm:px-6 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm sm:text-base transition-all hover:-translate-y-0.5 hover:shadow-md shadow-sm"
            >
              {loading ? "Loading..." : "Generate Report"}
            </button>
          </form>
          
          {hasSearched && report.length > 0 && (
            <div className="mt-4 flex gap-3 flex-wrap">
              <button
                onClick={exportToExcel}
                className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 flex items-center gap-2 font-medium text-sm sm:text-base transition-all hover:-translate-y-0.5 hover:shadow-md shadow-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Export to Excel
              </button>
              <button
                onClick={exportToPDF}
                className="px-5 py-2.5 bg-red-500 text-white rounded-xl hover:bg-red-600 flex items-center gap-2 font-medium text-sm sm:text-base transition-all hover:-translate-y-0.5 hover:shadow-md shadow-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Export to PDF
              </button>
            </div>
          )}
        </div>

        {hasSearched && report.length === 0 && (
          <div className="text-center py-10 text-slate-500 bg-white/80 backdrop-blur-2xl border border-white/40 rounded-2xl shadow-xl">
            No questions found for the selected date range
          </div>
        )}

        {report.map((question) => (
          <div
            key={question.questionId}
            className="bg-white/80 backdrop-blur-2xl border border-white/40 rounded-2xl shadow-xl mb-4 sm:mb-6 overflow-hidden"
          >
            <div className="bg-emerald-50/80 backdrop-blur-xl px-4 sm:px-6 py-3 sm:py-4 border-b border-emerald-100/60">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-2">
                <div className="flex-1">
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold text-slate-800">
                    {question.english}
                  </h3>
                  {question.arabic && (
                    <p className="text-slate-600 mt-1 font-noto text-sm">
                      {question.arabic}
                    </p>
                  )}
                </div>
                <div className="text-xs sm:text-sm text-slate-500 whitespace-nowrap">
                  {new Date(question.releaseDate).toLocaleDateString()}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 p-4 sm:p-6">
              {/* Correct Answers */}
              <div>
                <h4 className="text-sm sm:text-base font-semibold text-green-700 mb-3 flex items-center">
                  <span className="inline-block w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                  Correct Answers ({question.correctAnswers.length})
                </h4>
                {question.correctAnswers.length > 0 ? (
                  <div className="space-y-2">
                    {question.correctAnswers.map((user) => (
                      <div
                        key={user.userId}
                        className="bg-green-50/80 border border-green-200/60 rounded-xl p-3"
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 items-center">
                          <div className="font-medium text-slate-800 text-sm">
                            {user.name}
                          </div>
                          <div className="text-xs sm:text-sm text-slate-500 truncate">{user.email}</div>
                          <div className="text-sm font-semibold text-green-700 truncate">
                            {user.selectedOption}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm italic">
                    No correct answers
                  </p>
                )}
              </div>

              {/* Incorrect Answers */}
              <div>
                <h4 className="text-sm sm:text-base font-semibold text-red-700 mb-3 flex items-center">
                  <span className="inline-block w-3 h-3 bg-red-500 rounded-full mr-2"></span>
                  Incorrect Answers ({question.incorrectAnswers.length})
                </h4>
                {question.incorrectAnswers.length > 0 ? (
                  <div className="space-y-2">
                    {question.incorrectAnswers.map((user) => (
                      <div
                        key={user.userId}
                        className="bg-red-50/80 border border-red-200/60 rounded-xl p-3"
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-4 items-center">
                          <div className="font-medium text-slate-800 text-sm truncate">
                            {user.name}
                          </div>
                          <div className="text-xs sm:text-sm text-slate-500 truncate">{user.email}</div>
                          <div className="text-sm font-semibold text-red-700 truncate">
                            {user.selectedOption}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm italic">
                    No incorrect answers
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AnswersReport;
